import './os.css';
import { icon, type IconName } from './icons';
import { APPS, type AppCtx, type AppDef, type AppId, type Note } from './apps';
import { GREETINGS } from './greetings';
import { JOURNEY, OWNER, PROFILE, PROJECTS, SERVICES } from './data';
import { store as shared } from '../../system/store';

// SalmanOS: the studio computer. Not a real operating system: a custom, computer-like portfolio workspace that the
// studio pins onto a monitor's screen (or expands to full screen on small displays).
//   power-on: the mark draws itself, boot status, a 30-language greeting (skippable), then the desktop arrives in a
//   stagger (wallpaper settles, menu bar drops, the hero rises, widgets slide in, the dock springs up);
//   desktop: a living wallpaper (the city's real road network with traffic flowing along it), a menu bar with the brand
//   menu, a command palette (Ctrl/Cmd + K), notifications and a notification centre, widgets, a dock;
//   windows: open out of their dock tile, drag, snap to an edge (left / right half, top = full), resize, minimise,
//   maximise, keyboard support; synthesised interface sounds; Settings for theme, accent, wallpaper and motion.
// A monitor can pin one app open (the second screen's GitHub browser). Exit returns to the 3D studio.

// settings live in the site's store under "os." (system/store.ts: never throws, migrates the old "salmanos." keys)
const store = { get: (k: string) => shared.get(`os.${k}`), set: (k: string, v: string) => shared.set(`os.${k}`, v) };
const reducedPref = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
const dhaka = (o: Intl.DateTimeFormatOptions) => new Date().toLocaleString('en-GB', { timeZone: 'Asia/Dhaka', ...o });

/** Quiet interface sounds, synthesised (no audio files): click, open, close, notify and the power-on chime. */
class UiSound {
  private ctx: AudioContext | null = null;
  muted = store.get('muted') === '1';
  private ac() { this.ctx ??= new AudioContext(); if (this.ctx.state === 'suspended') void this.ctx.resume(); return this.ctx; }
  tone(f0: number, f1: number, dur: number, gain: number, type: OscillatorType = 'sine', delay = 0) {
    if (this.muted) return;
    const ctx = this.ac(), t = ctx.currentTime + delay, o = ctx.createOscillator(), g = ctx.createGain(), lp = ctx.createBiquadFilter();
    o.type = type; o.frequency.setValueAtTime(f0, t); o.frequency.exponentialRampToValueAtTime(f1, t + dur);
    lp.type = 'lowpass'; lp.frequency.value = 3200;
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(gain, t + 0.008); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(lp).connect(g).connect(ctx.destination); o.start(t); o.stop(t + dur + 0.02);
  }
  click() { this.tone(1800, 1200, 0.035, 0.035, 'triangle'); }
  open() { this.tone(520, 780, 0.16, 0.045); }
  close() { this.tone(700, 420, 0.14, 0.04); }
  notify() { this.tone(880, 1320, 0.12, 0.03); this.tone(1320, 1760, 0.18, 0.025, 'sine', 0.09); }
  chime() {
    if (this.muted) return;
    // a soft major-ninth swell: E4, B4, F#5, G#5
    [329.6, 493.9, 740, 830.6].forEach((f, i) => this.tone(f, f * 1.002, 2.4, 0.03 / (1 + i * 0.4), 'sine', i * 0.06));
  }
  toggle() { this.muted = !this.muted; store.set('muted', this.muted ? '1' : '0'); return this.muted; }
}

type Win = { def: AppDef; el: HTMLElement; body: HTMLElement; min: boolean; max: boolean };
type Rect = { left: number; top: number; width: number; height: number };

export class SalmanOS {
  readonly root: HTMLElement;
  onExit: () => void = () => {};
  active = false;
  private layer: { wake: HTMLElement; hello: HTMLElement; desktop: HTMLElement };
  private winLayer: HTMLElement;
  private dock: HTMLElement;
  private appName: HTMLElement;
  private windows = new Map<AppId, Win>();
  private z = 10;
  private sound = new UiSound();
  private skip: (() => void) | null = null;
  private clockTimer = 0;
  private wallDone = false;
  private pinned: AppId | null = null;
  private notes: Array<Note & { at: number }> = [];
  private overlay: HTMLElement | null = null;   // the open menu, palette or notification centre
  private launched = new Set<AppId>();

  constructor() {
    const root = document.createElement('div');
    root.className = 'sos';
    root.setAttribute('role', 'application');
    root.setAttribute('aria-label', 'Salman’s computer');
    root.hidden = true;
    root.dataset.theme = store.get('theme') ?? 'dark';
    root.dataset.accent = store.get('accent') ?? 'amber';
    root.dataset.wall = store.get('wall') ?? 'city';
    root.dataset.motion = store.get('motion') ?? 'full';
    root.innerHTML = `
      <div class="sos-layer sos-desktop">
        <div class="sos-wall" aria-hidden="true"><div class="map"></div><div class="aurora"></div><div class="vignette"></div><div class="grain"></div></div>
        <header class="sos-bar">
          <button class="sos-brand" data-act="menu" aria-haspopup="menu">${MARK_SMALL}SalmanOS</button><span class="sos-appname">Desktop</span><span class="sp"></span>
          <button class="btn find" data-act="find" aria-label="Search (Ctrl K)">${icon('search')}<span>Search</span><kbd>${isMac() ? '⌘' : 'Ctrl'} K</kbd></button>
          <button class="btn" data-act="bell" aria-label="Notifications">${icon('bell')}</button>
          <button class="btn" data-act="sound" aria-label="Sound">${icon(this.sound.muted ? 'speakerSlash' : 'speakerHigh')}</button>
          <button class="btn" data-act="theme" aria-label="Light or dark">${icon(root.dataset.theme === 'light' ? 'moon' : 'sun')}</button>
          <span class="sys" aria-hidden="true">${icon('wifi')}${icon('battery')}</span>
          <span class="clock" aria-label="Time in Dhaka"></span>
          <button class="btn exit" data-act="exit">${icon('power')}<span>Leave computer</span></button>
        </header>
        <section class="sos-home"></section>
        <aside class="sos-widgets"></aside>
        <div class="sos-wins"></div>
        <div class="sos-snap"></div>
        <div class="sos-toasts" aria-live="polite"></div>
        <nav class="sos-dock" aria-label="Apps"></nav>
      </div>
      <div class="sos-layer sos-hello gone" aria-hidden="true"></div>
      <div class="sos-layer sos-wake"><div class="sos-boot"><div class="sos-mark">${MARK}</div><div class="sos-progress"><i></i></div><div class="sos-status"></div></div></div>`;
    document.body.appendChild(root);
    this.root = root;
    this.layer = { wake: root.querySelector('.sos-wake')!, hello: root.querySelector('.sos-hello')!, desktop: root.querySelector('.sos-desktop')! };
    this.winLayer = root.querySelector('.sos-wins')!;
    this.dock = root.querySelector('.sos-dock')!;
    this.appName = root.querySelector('.sos-bar .sos-appname')!;
    this.buildHome();
    this.buildDock();
    const act = (a: string, fn: (b: HTMLElement) => void) => root.querySelector<HTMLElement>(`[data-act="${a}"]`)!.addEventListener('click', (e) => fn(e.currentTarget as HTMLElement));
    act('exit', () => this.onExit());
    act('sound', () => this.toggleSound());
    act('theme', () => this.toggleTheme());
    act('find', () => this.palette());
    act('bell', () => this.centre());
    act('menu', (b) => this.menu(b));
    // the wallpaper drifts a little with the pointer (depth): at most once a frame, a transform only
    let drift = 0, px = 0, py = 0;
    this.layer.desktop.addEventListener('pointermove', (e) => {
      if (this.motionOff() || e.pointerType !== 'mouse') return;
      px = e.clientX; py = e.clientY;
      drift ||= requestAnimationFrame(() => {
        drift = 0;
        const r = root.getBoundingClientRect(), x = (px - r.left) / r.width - 0.5, y = (py - r.top) / r.height - 0.5;
        (root.querySelector('.sos-wall .map') as HTMLElement).style.transform = `translate3d(${(-x * 14).toFixed(1)}px, ${(-y * 10).toFixed(1)}px, 0)`;
      });
    });
    // spotlight cards: a soft light follows the pointer
    root.addEventListener('pointermove', (e) => {
      const card = (e.target as HTMLElement).closest<HTMLElement>('.spot');
      if (!card) return;
      const r = card.getBoundingClientRect();
      card.style.setProperty('--mx', `${e.clientX - r.left}px`); card.style.setProperty('--my', `${e.clientY - r.top}px`);
    });
    // the computer owns the keyboard while it is on: nothing reaches the studio or the driving controls
    root.addEventListener('keydown', (e) => e.stopPropagation());
    addEventListener('keydown', (e) => this.onKey(e), true);
  }

  private motionOff() { return this.root.dataset.motion === 'reduced' || reducedPref(); }

  /** Place the screen: the monitor's projected rectangle, or the whole viewport (`full`). */
  setRect(r: Rect, full: boolean, bezelPx = 0) {
    Object.assign(this.root.style, { left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px` });
    this.root.classList.toggle('full', full);
    // the monitor's 8 mm matte bezel, at its true size in pixels (the baked one catches the monitor's spill light)
    this.root.style.boxShadow = full || !bezelPx ? 'none' : `0 0 0 ${bezelPx.toFixed(1)}px #08090a, 0 0 0 ${(bezelPx + 1).toFixed(1)}px rgba(255,255,255,.05)`;
  }

  /** Power on: the mark, boot status, the greeting, then the desktop; optionally open an app, or pin one open (it fills
   *  the screen and cannot be closed or minimised while this monitor is on). */
  async start(app?: AppId | 'home', pin?: AppId) {
    this.pinned = pin ?? null;
    this.active = true;
    this.root.hidden = false;
    this.root.style.opacity = '1';
    const { wake, hello, desktop } = this.layer;
    desktop.classList.remove('on');
    wake.classList.remove('gone', 'on');
    hello.classList.add('gone'); hello.innerHTML = '';
    const bar = wake.querySelector('.sos-progress i') as HTMLElement, status = wake.querySelector('.sos-status') as HTMLElement;
    bar.style.transform = 'scaleX(0)'; status.innerHTML = '';
    let skipped = false;
    this.skip = () => { skipped = true; };
    void this.buildWall();
    this.tickClock();
    // phase 1: the screen wakes - the mark draws itself while the workspace loads (status lines, a real progress bar
    // over the fonts and the wallpaper)
    await wait(60);
    wake.classList.add('on');
    this.sound.chime();
    const steps = ['Waking up', 'Loading workspace', 'Connecting to Dhaka', 'Starting apps'];
    const fonts = this.loadGreetingFonts();
    const fast = this.motionOff();
    for (let i = 0; i < steps.length && !skipped; i++) {
      status.innerHTML = `<span>${steps[i]}</span>`;
      bar.style.transform = `scaleX(${(i + 1) / steps.length})`;
      await this.until(() => skipped, fast ? 60 : 300);
    }
    await Promise.race([fonts, wait(900)]);
    // phase 2: hello in thirty languages (skippable)
    if (!skipped) {
      hello.classList.remove('gone');
      const skipBtn = document.createElement('button');
      skipBtn.className = 'sos-skip'; skipBtn.textContent = 'Skip intro';
      skipBtn.addEventListener('click', () => this.skip?.());
      hello.appendChild(skipBtn);
      const trail = document.createElement('div'); trail.className = 'trail'; hello.appendChild(trail);
      wake.classList.add('gone');
      if (fast) {
        this.word(hello, GREETINGS[0].word, 'latin', 1200);
        await this.until(() => skipped, 1200);
      } else {
        const step = 118;
        for (let i = 0; i < GREETINGS.length - 1 && !skipped; i++) {
          const g = GREETINGS[i];
          this.word(hello, g.word, g.script, step * 1.6, g.dir);
          if (i % 2 === 0) this.trailWord(trail, g.word, g.script);
          await this.until(() => skipped, step);
        }
        if (!skipped) {
          const last = GREETINGS[GREETINGS.length - 1];
          const w = this.word(hello, last.word, last.script, 0);
          w.classList.add('final');
          const sub = document.createElement('div'); sub.className = 'sub'; sub.textContent = 'Welcome to Salman’s workspace'; hello.appendChild(sub);
          requestAnimationFrame(() => sub.classList.add('on'));
          await this.until(() => skipped, 1000);
        }
      }
    }
    // phase 3: into the desktop (the arrival is staggered in CSS)
    this.skip = null;
    desktop.classList.add('on');
    hello.classList.add('gone'); wake.classList.add('gone');
    this.gradProgress();
    await wait(fast ? 0 : 450);
    hello.innerHTML = '';
    if (this.pinned) {
      this.open(this.pinned);
      const w = this.windows.get(this.pinned);
      if (w) { w.el.classList.add('pinned'); if (!w.max) this.toggleMax(this.pinned); }
      return;
    }
    if (app && app !== 'home') this.open(app);
    // a couple of friendly notifications, once per power-on
    setTimeout(() => this.active && this.notify({ title: 'Welcome to SalmanOS', body: `Press ${isMac() ? '⌘' : 'Ctrl'} K to search apps, projects and services.`, icon: 'sparkle' }), fast ? 300 : 1400);
    setTimeout(() => this.active && this.notify({ title: 'Battle Zone BD', body: 'Free Fire tournaments for Bangladesh — launching soon.', icon: 'trophy', tile: '#b8782a', action: () => this.open('projects', 'battlezone') }), fast ? 6000 : 7200);   // after the welcome note has gone
  }

  /** Power off: windows close, the screen goes dark. */
  async shutdown() {
    this.active = false;
    this.skip?.();
    this.closeOverlay();
    this.root.style.opacity = '0';
    await wait(this.motionOff() ? 0 : 380);
    for (const id of [...this.windows.keys()]) this.close(id, true);
    this.layer.desktop.classList.remove('on');
    this.root.querySelector('.sos-toasts')!.innerHTML = '';
    this.root.hidden = true;
    clearInterval(this.clockTimer);
  }

  private async until(done: () => boolean, ms: number) { const end = performance.now() + ms; while (performance.now() < end && !done()) await wait(16); }

  private word(host: HTMLElement, text: string, script: string, dur: number, dir?: string) {
    const w = document.createElement('div');
    w.className = 'word'; w.textContent = text; w.dataset.script = script;
    if (dir) w.dir = dir;
    if (dur) { w.style.setProperty('--dur', `${dur}ms`); setTimeout(() => w.remove(), dur + 40); }
    host.appendChild(w);
    return w;
  }

  private trailWord(host: HTMLElement, text: string, script: string) {
    const s = document.createElement('span');
    s.textContent = text; s.dataset.script = script;
    s.style.left = `${8 + Math.random() * 78}%`; s.style.top = `${10 + Math.random() * 74}%`;
    host.appendChild(s);
    setTimeout(() => s.remove(), 2500);
  }

  private loadGreetingFonts() {
    if (!('fonts' in document)) return Promise.resolve();
    const fams = ['OS Display', 'OS Greet Latin', 'OS Greet Bengali', 'OS Greet Arabic', 'OS Greet Devanagari', 'OS Greet JP', 'OS Greet KR', 'OS Greet SC', 'OS Greet Thai', 'OS Greet Tamil', 'OS Greet Hebrew'];
    const text = GREETINGS.map((g) => g.word).join('');
    return Promise.all(fams.map((f) => document.fonts.load(`300 48px "${f}"`, text).catch(() => []))).then(() => undefined);
  }

  // ------------------------------------------------------------------ desktop

  private buildHome() {
    const home = this.root.querySelector('.sos-home') as HTMLElement;
    home.innerHTML = `
      <p class="eyebrow"><span>[01]</span><span class="mk">Available for projects</span></p>
      <h1>Kazi Salman</h1>
      <p class="role">${PROFILE.role.split(' · ').map(esc).join(' / ')}</p>
      <p>I build websites, SaaS, e-commerce, AI automation and bots, and interactive 3D, including the city outside this window.</p>
      <div class="sos-quick">
        <button class="primary" data-open="work">${icon('rocket')} Start a project</button>
        <button data-open="projects">${icon('projects')} Projects</button>
        <button data-open="services">${icon('services')} Services</button>
      </div>
      <div class="sos-hint">${icon('command')} Press <kbd>${isMac() ? '⌘' : 'Ctrl'} K</kbd> to search everything</div>`;
    home.querySelectorAll<HTMLElement>('[data-open]').forEach((b) => b.addEventListener('click', () => this.open(b.dataset.open as AppId)));
    const widgets = this.root.querySelector('.sos-widgets') as HTMLElement;
    widgets.innerHTML = `
      <div class="sos-card"><h4>Dhaka <span>GMT+6</span></h4><div class="clockface"><svg viewBox="0 0 60 60" aria-hidden="true"><circle cx="30" cy="30" r="28" fill="none" stroke="currentColor" stroke-opacity=".18"/>${Array.from({ length: 12 }, (_, i) => `<line x1="30" y1="4.5" x2="30" y2="${i % 3 ? 7 : 9}" stroke="currentColor" stroke-opacity="${i % 3 ? 0.3 : 0.6}" stroke-width="${i % 3 ? 1 : 1.6}" transform="rotate(${i * 30} 30 30)"/>`).join('')}<line data-h x1="30" y1="30" x2="30" y2="16" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/><line data-m x1="30" y1="30" x2="30" y2="9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle cx="30" cy="30" r="2.2" fill="var(--accent)"/></svg><div><div class="big" data-clock></div><div style="color:var(--text-2);font-size:12px;margin-top:6px" data-date></div></div></div></div>
      <div class="sos-card"><h4>Now</h4>
        <div class="row"><span>Studying</span><span>${esc(PROFILE.semester)}</span></div>
        <div class="row"><span>Running</span><span class="live">● Nongorr</span></div>
        <div class="row"><span>Launching</span><span>Battle Zone BD</span></div>
        <div class="row"><span>Building</span><span>Starline · this city</span></div>
      </div>
      <div class="sos-card"><h4>Degree <span>2023 → 2027</span></h4><div class="grad"><b style="font-size:13.5px">B.Sc. Computer Science</b><div class="bar"><i></i></div><small><span>BRAC University</span><span>${esc(PROFILE.graduation)}</span></small></div></div>`;
  }

  /** The degree bar: from admission (2023) to the expected graduation (2027), measured in whole years. */
  private gradProgress() {
    const now = new Date(), y = now.getFullYear() + now.getMonth() / 12, p = Math.max(0, Math.min(1, (y - 2023) / (2027.5 - 2023)));
    const i = this.root.querySelector<HTMLElement>('.sos-card .grad .bar i');
    if (i) { i.style.setProperty('--p', '0%'); requestAnimationFrame(() => i.style.setProperty('--p', `${(p * 100).toFixed(0)}%`)); }
  }

  private tickClock() {
    const set = () => {
      const t = dhaka({ hour: '2-digit', minute: '2-digit' });
      (this.root.querySelector('.sos-bar .clock') as HTMLElement).textContent = `${dhaka({ weekday: 'short', day: 'numeric', month: 'short' })}  ${t}`;
      const c = this.root.querySelector('[data-clock]'), dd = this.root.querySelector('[data-date]');
      if (c) c.textContent = t;
      if (dd) dd.textContent = dhaka({ weekday: 'long', day: 'numeric', month: 'long' });
      const h = Number(dhaka({ hour: 'numeric', hour12: false })) % 12, m = Number(dhaka({ minute: 'numeric' }));
      this.root.querySelector('[data-h]')?.setAttribute('transform', `rotate(${(h + m / 60) * 30} 30 30)`);
      this.root.querySelector('[data-m]')?.setAttribute('transform', `rotate(${m * 6} 30 30)`);
    };
    set();
    clearInterval(this.clockTimer);
    this.clockTimer = window.setInterval(set, 10000);
  }

  /** Wallpaper: the city's own road network as fine linework (the same layout the game drives on), with traffic -
   *  small lights - flowing along the main roads. */
  private async buildWall() {
    if (this.wallDone) return;
    this.wallDone = true;
    const host = this.root.querySelector('.sos-wall .map') as HTMLElement;
    try {
      const layout = await (await fetch('/data/PLAYABLE_WORLD_LAYOUT_C.json')).json() as { roads: Array<{ id: string; label: string; width_m: number; road_class: string; centerline: Array<{ x: number; z: number }>; cross_section?: { oneway?: boolean } }>; water_paths?: Array<{ points: Array<{ x: number; z: number }> }>; water_bodies?: Array<{ polygon?: Array<{ x: number; z: number }> }> };
      const pts = layout.roads.flatMap((r) => r.centerline);
      const minX = Math.min(...pts.map((p) => p.x)), maxX = Math.max(...pts.map((p) => p.x)), minZ = Math.min(...pts.map((p) => p.z)), maxZ = Math.max(...pts.map((p) => p.z));
      const pad = 600, w = maxX - minX + pad * 2, h = maxZ - minZ + pad * 2;
      const P = (p: { x: number; z: number }) => `${(p.x - minX + pad).toFixed(1)},${(p.z - minZ + pad).toFixed(1)}`;
      const light = this.root.dataset.theme === 'light';
      const line = light ? '#16201f' : '#ece6d8', accent = getComputedStyle(this.root).getPropertyValue('--accent').trim() || '#e2a14a', water = light ? '#23786c' : '#5bb3a6';
      const roads = layout.roads.map((r) => `<polyline points="${r.centerline.map(P).join(' ')}" fill="none" stroke="${r.road_class === 'expressway' ? accent : line}" stroke-opacity="${r.road_class === 'expressway' ? 0.62 : r.cross_section?.oneway ? 0.22 : 0.32}" stroke-width="${Math.max(9, r.width_m * 0.7)}" stroke-linecap="round" stroke-linejoin="round"/>`).join('');
      const lakes = (layout.water_bodies ?? []).filter((b) => b.polygon).map((b) => `<polygon points="${b.polygon!.map(P).join(' ')}" fill="${water}" fill-opacity=".16"/>`).join('');
      const canals = (layout.water_paths ?? []).map((c) => `<polyline points="${c.points.map(P).join(' ')}" fill="none" stroke="${water}" stroke-opacity=".26" stroke-width="12"/>`).join('');
      const grid = Array.from({ length: Math.ceil(w / 500) }, (_, i) => `<line x1="${i * 500}" y1="0" x2="${i * 500}" y2="${h}"/>`).join('') + Array.from({ length: Math.ceil(h / 500) }, (_, i) => `<line x1="0" y1="${i * 500}" x2="${w}" y2="${i * 500}"/>`).join('');
      const named = layout.roads.filter((r) => ['expressway', 'arterial'].includes(r.road_class)).map((r) => {
        const m = r.centerline[Math.floor(r.centerline.length * 0.42)], n = r.centerline[Math.min(r.centerline.length - 1, Math.floor(r.centerline.length * 0.42) + 3)];
        let ang = Math.atan2(n.z - m.z, n.x - m.x) * 57.3; if (ang > 90) ang -= 180; if (ang < -90) ang += 180;
        const [x, y] = P(m).split(',');
        return `<text transform="translate(${x} ${y}) rotate(${ang.toFixed(1)})" dy="-34" text-anchor="middle" fill="${line}" fill-opacity=".40" font-family="OS Sans, Inter, sans-serif" font-size="46" letter-spacing="6">${esc(r.label.toUpperCase())}</text>`;
      }).join('');
      // traffic: lights moving along the main roads, both ways, at road speed
      const main = layout.roads.filter((r) => ['expressway', 'arterial'].includes(r.road_class));
      const paths = main.map((r, i) => `<path id="sos-r${i}" d="M${r.centerline.map(P).join(' L')}" fill="none"/>`).join('');
      const cars = this.motionOff() ? '' : main.flatMap((r, i) => {
        let len = 0; for (let k = 1; k < r.centerline.length; k++) len += Math.hypot(r.centerline[k].x - r.centerline[k - 1].x, r.centerline[k].z - r.centerline[k - 1].z);
        const n = Math.max(1, Math.round(len / 900)), dur = Math.max(18, len / 60);
        return Array.from({ length: n * 2 }, (_, k) => {
          const back = k % 2 === 1, col = back ? '#ff6b57' : (light ? '#1b2a2a' : '#fff4dc');
          return `<circle r="${r.road_class === 'expressway' ? 13 : 10}" fill="${col}" fill-opacity="${back ? 0.75 : 0.9}"><animateMotion dur="${dur.toFixed(1)}s" begin="-${((k / (n * 2)) * dur).toFixed(1)}s" repeatCount="indefinite" ${back ? 'keyPoints="1;0" keyTimes="0;1" calcMode="linear"' : ''}><mpath href="#sos-r${i}"/></animateMotion></circle>`;
        });
      }).join('');
      host.innerHTML = `<svg viewBox="0 0 ${w.toFixed(0)} ${h.toFixed(0)}" preserveAspectRatio="xMaxYMid slice" xmlns="http://www.w3.org/2000/svg">
        <defs>${paths}</defs>
        <g stroke="${line}" stroke-opacity=".035" stroke-width="2">${grid}</g>${lakes}${canals}${roads}${named}
        <g>${cars}</g>
        <text x="${w - 90}" y="${h - 120}" text-anchor="end" fill="${line}" fill-opacity=".32" font-family="OS Sans, Inter, sans-serif" font-size="78" letter-spacing="10">DHAKA · 23.78° N 90.42° E</text></svg>`;
    } catch { host.innerHTML = ''; }
  }

  private buildDock() {
    for (const def of APPS) {
      if (def.hidden) continue;
      const b = document.createElement('button');
      b.className = 'sos-tile'; b.dataset.app = def.id; b.setAttribute('aria-label', def.title);
      b.style.setProperty('--tile', def.tile);
      b.innerHTML = `${icon(def.icon)}<span class="tip">${def.title}</span>`;
      b.addEventListener('click', () => this.open(def.id));
      this.dock.appendChild(b);
      if (def.sepAfter) { const s = document.createElement('span'); s.className = 'sos-sep'; this.dock.appendChild(s); }
    }
    // no magnification: it measured every tile on every pointer move (forced layouts, a laggy dock); a hovered tile lifts
    // in CSS instead, on the compositor
  }

  // ------------------------------------------------------------------ menu, palette, notifications

  private closeOverlay() { this.overlay?.remove(); this.overlay = null; this.root.querySelector('.sos-brand')?.classList.remove('open'); }

  private menu(anchor: HTMLElement) {
    if (this.overlay?.classList.contains('sos-menu')) { this.closeOverlay(); return; }
    this.closeOverlay();
    const m = document.createElement('div');
    m.className = 'sos-menu'; m.setAttribute('role', 'menu');
    const k = isMac() ? '⌘' : 'Ctrl';
    m.innerHTML = `
      <button data-m="about">${icon('about')} About Kazi Salman</button>
      <button data-m="settings">${icon('gear')} Settings…</button>
      <button data-m="find">${icon('search')} Search <kbd>${k} K</kbd></button>
      <hr><button data-m="work">${icon('rocket')} Start a project</button>
      <button data-m="football">${icon('football')} Penalty shoot-out</button>
      <hr><button data-m="exit">${icon('power')} Leave computer <kbd>Esc</kbd></button>`;
    m.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-m]'); if (!b) return;
      this.closeOverlay(); this.sound.click();
      const v = b.dataset.m!;
      if (v === 'find') this.palette(); else if (v === 'exit') this.onExit(); else this.open(v as AppId);
    });
    this.root.querySelector('.sos-desktop')!.appendChild(m);
    this.overlay = m; anchor.classList.add('open');
    setTimeout(() => addEventListener('pointerdown', (e) => { if (this.overlay === m && !m.contains(e.target as Node) && !anchor.contains(e.target as Node)) this.closeOverlay(); }, { once: true, capture: true }), 0);
  }

  /** Search everything: apps, projects, services, journey and actions. Arrow keys and Enter; Esc closes. */
  palette() {
    if (this.overlay?.classList.contains('sos-palette')) { this.closeOverlay(); return; }
    this.closeOverlay();
    type Item = { group: string; title: string; hint: string; icon: IconName; tile?: string; run: () => void; words: string };
    const items: Item[] = [
      ...APPS.filter((a) => !a.hidden || a.id === 'settings').map((a) => ({ group: 'Apps', title: a.title, hint: 'Open', icon: a.icon, tile: a.tile, run: () => this.open(a.id), words: `${a.title} ${a.id} ${a.keywords ?? ''}` })),
      ...PROJECTS.map((p) => ({ group: 'Projects', title: p.name, hint: p.status, icon: 'projects' as IconName, tile: '#b8782a', run: () => this.open('projects', p.id), words: `${p.name} ${p.kicker} ${p.stack.join(' ')}` })),
      ...SERVICES.map((s) => ({ group: 'Services', title: s.title, hint: s.what, icon: s.icon as IconName, tile: '#6b4f3a', run: () => this.open('services', s.id), words: `${s.title} ${s.what} ${s.pitch}` })),
      ...JOURNEY.map((j) => ({ group: 'Journey', title: `${j.when} · ${j.title}`, hint: j.org, icon: 'career' as IconName, tile: '#3c5a73', run: () => this.open('career'), words: `${j.when} ${j.title} ${j.org}` })),
      { group: 'Actions', title: 'Start a project', hint: 'Brief', icon: 'rocket', tile: '#a0522d', run: () => this.open('work'), words: 'hire contact brief quote' },
      { group: 'Actions', title: 'Message on WhatsApp', hint: OWNER.whatsappDisplay, icon: 'whatsapp', tile: '#1d6b52', run: () => window.open(`https://wa.me/${OWNER.whatsapp}`, '_blank', 'noopener'), words: 'whatsapp chat message call' },
      { group: 'Actions', title: 'Copy email address', hint: OWNER.email, icon: 'envelope', tile: '#2e7d5b', run: () => { void navigator.clipboard?.writeText(OWNER.email).catch(() => undefined); this.notify({ title: 'Copied', body: OWNER.email, icon: 'check', tile: '#2e7d5b' }); }, words: 'email mail copy' },
      { group: 'Actions', title: `Switch to ${this.root.dataset.theme === 'light' ? 'dark' : 'light'} mode`, hint: 'Theme', icon: 'sun', run: () => this.toggleTheme(), words: 'theme dark light mode' },
      { group: 'Actions', title: this.sound.muted ? 'Turn sound on' : 'Mute sound', hint: 'Sound', icon: 'speakerHigh', run: () => this.toggleSound(), words: 'sound mute audio' },
      { group: 'Actions', title: 'Play a penalty shoot-out', hint: 'Football', icon: 'football', tile: '#1f7a4c', run: () => this.open('football'), words: 'football soccer game play penalty goal' },
      { group: 'Actions', title: 'Leave the computer', hint: 'Esc', icon: 'power', run: () => this.onExit(), words: 'exit leave quit studio back' },
    ];
    const p = document.createElement('div');
    p.className = 'sos-palette';
    p.innerHTML = `<div class="box" role="dialog" aria-label="Search"><div class="q">${icon('search')}<input placeholder="Search apps, projects, services…" aria-label="Search" spellcheck="false" autocomplete="off" /><kbd>Esc</kbd></div><div class="res" role="listbox"></div>
      <div class="foot"><span><kbd>↑</kbd><kbd>↓</kbd> move</span><span><kbd>Enter</kbd> open</span><span><kbd>Esc</kbd> close</span></div></div>`;
    const input = p.querySelector('input') as HTMLInputElement, res = p.querySelector('.res') as HTMLElement;
    let shown: Item[] = [], sel = 0;
    const score = (it: Item, q: string) => {
      if (!q) return it.group === 'Apps' || it.group === 'Actions' ? 1 : 0;
      const t = it.title.toLowerCase(), w = it.words.toLowerCase();
      if (t.startsWith(q)) return 4;
      if (t.split(/[\s·—-]+/).some((x) => x.startsWith(q))) return 3;
      if (w.includes(q)) return 2;
      // letters in order (a small fuzzy match)
      let i = 0; for (const ch of t) if (ch === q[i]) i++;
      return i === q.length ? 1 : 0;
    };
    const render = () => {
      const q = input.value.trim().toLowerCase();
      shown = items.map((it) => ({ it, s: score(it, q) })).filter((x) => x.s > 0).sort((a, b) => b.s - a.s).slice(0, 40).map((x) => x.it);
      sel = Math.min(sel, Math.max(0, shown.length - 1));
      if (!shown.length) { res.innerHTML = `<div class="empty">No results for “${esc(input.value)}”</div>`; return; }
      let last = '', html = '';
      shown.forEach((it, i) => {
        if (it.group !== last) { html += `<div class="grp">${it.group}</div>`; last = it.group; }
        html += `<button class="it${i === sel ? ' sel' : ''}" data-i="${i}" role="option"><span class="g" style="--tile:${it.tile ?? 'var(--raised-2)'}">${icon(it.icon)}</span><b>${esc(it.title)}</b><small>${esc(it.hint)}</small></button>`;
      });
      res.innerHTML = html;
      res.querySelector('.sel')?.scrollIntoView({ block: 'nearest' });
    };
    const go = (i: number) => { const it = shown[i]; if (!it) return; this.closeOverlay(); this.sound.click(); it.run(); };
    input.addEventListener('input', () => { sel = 0; render(); });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { sel = Math.min(shown.length - 1, sel + 1); render(); e.preventDefault(); }
      else if (e.key === 'ArrowUp') { sel = Math.max(0, sel - 1); render(); e.preventDefault(); }
      else if (e.key === 'Enter') { go(sel); e.preventDefault(); }
    });
    res.addEventListener('click', (e) => { const b = (e.target as HTMLElement).closest<HTMLElement>('[data-i]'); if (b) go(Number(b.dataset.i)); });
    res.addEventListener('pointermove', (e) => { const b = (e.target as HTMLElement).closest<HTMLElement>('[data-i]'); if (b && Number(b.dataset.i) !== sel) { sel = Number(b.dataset.i); res.querySelectorAll('.it').forEach((x, i) => x.classList.toggle('sel', i === sel)); } });
    p.addEventListener('pointerdown', (e) => { if (e.target === p) this.closeOverlay(); });
    this.root.querySelector('.sos-desktop')!.appendChild(p);
    this.overlay = p;
    render();
    setTimeout(() => input.focus(), 20);
  }

  /** A notification: a toast at the top right (5 s), kept in the notification centre. */
  notify(n: Note, toast = true) {
    if (!this.active) return;
    const note = { ...n, at: Date.now() };
    this.notes.unshift(note); this.notes.length = Math.min(this.notes.length, 20);
    this.root.querySelector('[data-act="bell"]')?.classList.add('dot');
    if (!toast) return;   // kept in the notification centre only: the bell shows a dot
    const t = this.toastEl(note);
    const host = this.root.querySelector('.sos-toasts') as HTMLElement;
    host.prepend(t);
    while (host.children.length > 1) host.lastElementChild?.remove();   // one at a time, so the widgets stay readable
    this.sound.notify();
    setTimeout(() => { t.classList.add('out'); setTimeout(() => t.remove(), 300); }, 5200);
  }

  private toastEl(n: Note & { at: number }) {
    const t = document.createElement('div');
    t.className = 'sos-toast';
    t.innerHTML = `<span class="g" style="--tile:${n.tile ?? 'var(--accent)'}">${icon(n.icon ?? 'bell')}</span><div><b>${esc(n.title)}</b><span>${esc(n.body)}</span></div><time>${new Date(n.at).toLocaleTimeString('en-GB', { timeZone: 'Asia/Dhaka', hour: '2-digit', minute: '2-digit' })}</time>`;
    t.addEventListener('click', () => { this.sound.click(); t.remove(); n.action?.(); });
    return t;
  }

  private centre() {
    if (this.overlay?.classList.contains('sos-center')) { this.closeOverlay(); return; }
    this.closeOverlay();
    this.root.querySelector('[data-act="bell"]')?.classList.remove('dot');
    const c = document.createElement('div');
    c.className = 'sos-center';
    c.innerHTML = `<h5><span>Notifications</span><button data-clear style="color:var(--accent)">Clear</button></h5>`;
    if (!this.notes.length) c.insertAdjacentHTML('beforeend', `<div style="color:var(--muted);padding:12px 4px">You are all caught up.</div>`);
    for (const n of this.notes) c.appendChild(this.toastEl(n));
    c.querySelector('[data-clear]')!.addEventListener('click', () => { this.notes = []; this.closeOverlay(); });
    this.root.querySelector('.sos-desktop')!.appendChild(c);
    this.overlay = c;
    setTimeout(() => addEventListener('pointerdown', (e) => { if (this.overlay === c && !c.contains(e.target as Node) && !(e.target as HTMLElement).closest('[data-act="bell"]')) this.closeOverlay(); }, { once: true, capture: true }), 0);
  }

  // ------------------------------------------------------------------ settings

  toggleTheme() {
    const t = this.root.dataset.theme === 'light' ? 'dark' : 'light';
    this.setting('theme', t);
    return t;
  }
  toggleSound() {
    const m = this.sound.toggle();
    (this.root.querySelector('[data-act="sound"]') as HTMLElement).innerHTML = icon(m ? 'speakerSlash' : 'speakerHigh');
    this.sound.click();
    return m;
  }
  /** Apply and remember a setting (theme, accent, wall, motion). */
  setting(k: 'theme' | 'accent' | 'wall' | 'motion', v: string) {
    this.root.dataset[k] = v; store.set(k, v);
    if (k === 'theme') (this.root.querySelector('[data-act="theme"]') as HTMLElement).innerHTML = icon(v === 'light' ? 'moon' : 'sun');
    if (k === 'theme' || k === 'accent' || k === 'motion') { this.wallDone = false; void this.buildWall(); }
    this.sound.click();
  }

  // ------------------------------------------------------------------ windows

  private ctx: AppCtx = {
    open: (id, arg) => this.open(id, arg), exit: () => this.onExit(), click: () => this.sound.click(), notify: (n) => this.notify(n),
    tone: (f0, f1, d, g, type) => this.sound.tone(f0, f1, d, g, type),
    get: (k) => (k === 'sound' ? (this.sound.muted ? 'off' : 'on') : this.root.dataset[k] ?? ''),
    set: (k, v) => { if (k === 'sound') { if ((v === 'off') !== this.sound.muted) this.toggleSound(); } else this.setting(k, v); },
    palette: () => this.palette(),
  };

  open(id: AppId, arg?: string) {
    const def = APPS.find((a) => a.id === id);
    if (!def) return;
    this.closeOverlay();
    const existing = this.windows.get(id);
    if (existing) {
      if (existing.min) { existing.min = false; existing.el.style.display = ''; existing.el.classList.remove('min'); }
      if (arg) def.render(existing.body, this.ctx, arg);
      this.focus(id);
      return;
    }
    const desk = this.root.getBoundingClientRect();
    const barH = 34, dockH = 76;
    const W = Math.min(def.size[0], desk.width - 32), H = Math.min(def.size[1], desk.height - barH - dockH - 20);
    const n = this.windows.size;
    const left = Math.max(12, Math.min(desk.width - W - 12, (desk.width - W) / 2 + (n % 5) * 26 - 52));
    const top = Math.max(barH + 10, Math.min(desk.height - dockH - H - 8, barH + (desk.height - barH - dockH - H) / 2 + (n % 5) * 22 - 30));   // always above the dock
    const el = document.createElement('section');
    el.className = 'sos-win'; el.setAttribute('role', 'dialog'); el.setAttribute('aria-label', def.title);
    Object.assign(el.style, { left: `${left}px`, top: `${top}px`, width: `${W}px`, height: `${H}px` });
    el.style.setProperty('--tile', def.tile);
    // grow out of its dock tile
    const tile = this.dock.querySelector<HTMLElement>(`[data-app="${id}"]`);
    const tr = tile?.getBoundingClientRect();
    if (tr) { el.style.setProperty('--ox', `${tr.left + tr.width / 2 - desk.left - left}px`); el.style.setProperty('--oy', `${tr.top - desk.top - top}px`); }
    if (tile && !this.launched.has(id) && !this.motionOff()) { tile.classList.remove('bounce'); void tile.offsetWidth; tile.classList.add('bounce'); }
    this.launched.add(id);
    el.innerHTML = `<div class="sos-titlebar"><div class="sos-title"><span class="glyph">${icon(def.icon)}</span>${def.title}${def.sub ? `<small>${def.sub}</small>` : ''}</div>
      <div class="sos-ctl"><button class="minb" aria-label="Minimise">${icon('minus')}</button><button class="max" aria-label="Maximise">${icon('cornersOut')}</button><button class="close" aria-label="Close">${icon('x')}</button></div></div>
      <div class="sos-body"></div><div class="sos-grip" aria-hidden="true"></div>`;
    const body = el.querySelector('.sos-body') as HTMLElement;
    const win: Win = { def, el, body, min: false, max: false };
    this.windows.set(id, win);
    this.winLayer.appendChild(el);
    def.render(body, this.ctx, arg);
    el.querySelector('.close')!.addEventListener('click', () => this.close(id));
    el.querySelector('.minb')!.addEventListener('click', () => this.minimise(id));
    el.querySelector('.max')!.addEventListener('click', () => this.toggleMax(id));
    el.addEventListener('pointerdown', () => this.focus(id));
    this.drag(win);
    this.resize(win);
    this.focus(id);
    this.sound.open();
    tile?.classList.add('running');
  }

  private focus(id: AppId) {
    for (const [k, w] of this.windows) w.el.classList.toggle('blur', k !== id);
    const w = this.windows.get(id);
    if (!w) return;
    w.el.style.zIndex = String(++this.z);
    this.appName.textContent = w.def.title;
  }

  private top(): AppId | null {
    let best: AppId | null = null, z = -1;
    for (const [k, w] of this.windows) if (!w.min && Number(w.el.style.zIndex) > z) { z = Number(w.el.style.zIndex); best = k; }
    return best;
  }

  close(id: AppId, instant = false) {
    const w = this.windows.get(id);
    if (!w || (id === this.pinned && !instant)) return;
    this.windows.delete(id);
    w.def.dispose?.(w.body);
    this.dock.querySelector(`[data-app="${id}"]`)?.classList.remove('running');
    if (instant || this.motionOff()) w.el.remove(); else { w.el.classList.add('closing'); setTimeout(() => w.el.remove(), 200); this.sound.close(); }
    const next = this.top();
    if (next) this.focus(next); else this.appName.textContent = 'Desktop';
  }

  private minimise(id: AppId) {
    const w = this.windows.get(id);
    if (!w || id === this.pinned) return;
    w.min = true; w.el.classList.add('min'); this.sound.close();
    setTimeout(() => { if (w.min) w.el.style.display = 'none'; }, 300);
    const next = this.top();
    if (next) this.focus(next); else this.appName.textContent = 'Desktop';
  }

  private toggleMax(id: AppId) {
    const w = this.windows.get(id);
    if (!w) return;
    w.max = !w.max; w.el.classList.toggle('max', w.max);
    (w.el.querySelector('.max') as HTMLElement).innerHTML = icon(w.max ? 'cornersIn' : 'cornersOut');
    this.sound.click();
  }

  /** Drag by the title bar; dragged to an edge it snaps (left or right half, top = maximise), with a preview. */
  private drag(w: Win) {
    const bar = w.el.querySelector('.sos-titlebar') as HTMLElement, snap = this.root.querySelector('.sos-snap') as HTMLElement;
    bar.addEventListener('dblclick', (e) => { if (!(e.target as HTMLElement).closest('button')) this.toggleMax(w.def.id); });
    bar.addEventListener('pointerdown', (e) => {
      if ((e.target as HTMLElement).closest('button') || this.root.getBoundingClientRect().width <= 760 || w.def.id === this.pinned) return;
      const desk = this.root.getBoundingClientRect();
      if (w.max) {
        // dragging a maximised window out restores it under the pointer
        w.max = false; w.el.classList.remove('max'); (w.el.querySelector('.max') as HTMLElement).innerHTML = icon('cornersOut');
        w.el.style.left = `${e.clientX - desk.left - w.el.offsetWidth / 2}px`; w.el.style.top = `${e.clientY - desk.top - 20}px`;
      }
      const start = { x: e.clientX, y: e.clientY, l: w.el.offsetLeft, t: w.el.offsetTop };
      bar.setPointerCapture(e.pointerId);
      let zone: 'left' | 'right' | 'top' | null = null;
      const barH = 34, dockH = 84;
      const zoneRect = (z: typeof zone): Rect | null => z === 'left' ? { left: 8, top: barH + 8, width: desk.width / 2 - 12, height: desk.height - barH - dockH } : z === 'right' ? { left: desk.width / 2 + 4, top: barH + 8, width: desk.width / 2 - 12, height: desk.height - barH - dockH } : z === 'top' ? { left: 8, top: barH + 8, width: desk.width - 16, height: desk.height - barH - dockH } : null;
      const move = (ev: PointerEvent) => {
        const l = Math.min(Math.max(start.l + ev.clientX - start.x, 40 - w.el.offsetWidth), desk.width - 40);
        const t = Math.min(Math.max(start.t + ev.clientY - start.y, barH), desk.height - 60);
        w.el.style.left = `${l}px`; w.el.style.top = `${t}px`;
        const px = ev.clientX - desk.left, py = ev.clientY - desk.top;
        const z = px < 14 ? 'left' : px > desk.width - 14 ? 'right' : py < barH + 6 ? 'top' : null;
        if (z !== zone) {
          zone = z;
          const r = zoneRect(z);
          if (r) Object.assign(snap.style, { left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px` });
          snap.classList.toggle('on', Boolean(r));
        }
      };
      const up = () => {
        bar.removeEventListener('pointermove', move); bar.removeEventListener('pointerup', up);
        snap.classList.remove('on');
        if (zone === 'top') { this.toggleMax(w.def.id); return; }
        const r = zoneRect(zone);
        if (r) {
          w.el.classList.add('snapping');
          Object.assign(w.el.style, { left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px` });
          setTimeout(() => w.el.classList.remove('snapping'), 340);
          this.sound.click();
        }
      };
      bar.addEventListener('pointermove', move); bar.addEventListener('pointerup', up);
    });
  }

  private resize(w: Win) {
    const grip = w.el.querySelector('.sos-grip') as HTMLElement;
    grip.addEventListener('pointerdown', (e) => {
      if (w.max) return;
      e.stopPropagation();
      grip.setPointerCapture(e.pointerId);
      const desk = this.root.getBoundingClientRect(), s = { x: e.clientX, y: e.clientY, w: w.el.offsetWidth, h: w.el.offsetHeight };
      const move = (ev: PointerEvent) => {
        w.el.style.width = `${Math.max(340, Math.min(desk.width - w.el.offsetLeft - 8, s.w + ev.clientX - s.x))}px`;
        w.el.style.height = `${Math.max(240, Math.min(desk.height - w.el.offsetTop - 8, s.h + ev.clientY - s.y))}px`;
      };
      const up = () => { grip.removeEventListener('pointermove', move); grip.removeEventListener('pointerup', up); };
      grip.addEventListener('pointermove', move); grip.addEventListener('pointerup', up);
    });
  }

  private onKey(e: KeyboardEvent) {
    if (!this.active) return;
    if (this.skip && ['Escape', 'Enter', ' '].includes(e.key)) { this.skip(); e.stopPropagation(); e.preventDefault(); return; }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); e.stopPropagation(); this.palette(); return; }
    if (e.key === 'Escape') {
      e.stopPropagation(); e.preventDefault();
      if (this.overlay) { this.closeOverlay(); return; }
      const lb = this.root.querySelector('.lightbox');
      if (lb) { lb.remove(); return; }
      const t = this.top();
      if (t && t !== this.pinned) this.close(t); else this.onExit();
    }
  }
}

function isMac() { return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent); }

// the power-on mark: a ring that draws itself, an italic S in the display face, a soft halo
const MARK = `<svg viewBox="0 0 56 56" aria-hidden="true"><circle class="halo" cx="28" cy="28" r="27" fill="none" stroke="var(--accent)" stroke-width="6" stroke-opacity=".5" filter="blur(6px)"/><circle class="ring" cx="28" cy="28" r="26.5" fill="none" stroke="currentColor" stroke-opacity=".55" stroke-width="1.2" stroke-linecap="round" transform="rotate(-90 28 28)"/><text class="glyph" x="28" y="38.5" text-anchor="middle" font-family="OS Display, Instrument Serif, serif" font-style="italic" font-size="32" fill="currentColor">S</text></svg>`;
const MARK_SMALL = `<svg viewBox="0 0 56 56" aria-hidden="true"><circle cx="28" cy="28" r="26.5" fill="none" stroke="currentColor" stroke-opacity=".5" stroke-width="3"/><text x="28" y="39" text-anchor="middle" font-family="OS Display, Instrument Serif, serif" font-style="italic" font-size="34" fill="currentColor">S</text></svg>`;
